import { cloneDeep, compact, get, has, isEmpty, omit, set, trim, unset, update } from 'lodash'
import { createContext, useContext, useEffect, useState } from 'react'
import { useQueries, useQuery, UseQueryOptions } from 'react-query'
import { useParams } from 'react-router-dom'
// @ts-expect-error
import { breadth } from 'treeverse'

import { getCurrentSpaceId } from '@/components/App/selectors'
import { useRoles } from '@/components/RolesContainer'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import {
  App,
  AppInstanceDataSource,
  BasicValidationError,
  CustomNodes,
  ExecutionStepStatus,
  Feature,
  FeatureExecutionStep,
  FeatureStatus,
  FieldTypes,
  FlowElement,
  Step,
  StepType,
  TriggerConfig,
  TriggerInput,
  TriggerTypes,
  Variable,
  Variables,
  Workflow,
  WorkgridCatalogDatasource,
  WorkgridDatasource
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import isAppOwner from '@/pages/SpaceAppBuilder/utilities/isAppOwner'
import { clearLocalStorageTriggerData } from '@/pages/SpaceAppBuilder/utilities/localStoragePreviousTriggerData'
import {
  dataSourceV3CatalogQueryOptions,
  dataSourceV3QueryOptions,
  featureQueryOptions,
  getFetchAppQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { getCurrentUser } from '@/store/selectors'

import * as actions from './actions'
import { UpdateFeatureProps } from './actions'
import { convertFeatureToVisualWorkflow } from './featureToVisualWorkflow'
import getOperations from './Operations/getOperations'
import {
  getAppBuilderDataSourcesMappingData,
  getAppBuilderFeatureData,
  getAppBuilderFeatureExecutionData
} from './selectors'
import {
  assignStepCount,
  constructOnSuccessChain,
  findNestedObject,
  findPath,
  flattenFeatureSteps,
  removeLastPath,
  traverseSteps,
  updateDuplicateStepIDs,
  updateNestedStepVariableReferences,
  validateNodes
} from './utils'
import {
  buildApplicationVariables,
  buildConfigVariables,
  buildDataSourceConfigVariables,
  buildDataSourceStepVariables,
  buildDefaultStepVariables,
  buildFeatureTriggerVariables,
  buildFeatureVariables,
  buildHttpTriggerVariables,
  buildKnowledgeEventVariables,
  buildLoopStepVariables,
  buildManualEventVariables,
  buildMicroappEventVariables,
  buildNotificationActionTriggerVariables,
  buildScheduledEventTriggerVariables,
  buildWorkgridDataSourceTriggerVariables,
  getVariablesFromJsonSchema
} from './variableBuilder'

const WorkflowFeatureContext = createContext<any>(undefined)

const WorkflowFeatureProviderBase = ({ children, defaultSelectedElement }: any) => {
  const { spaceId, appId, templateId, featureId } = useParams<{
    spaceId: string
    appId: string
    templateId: string
    featureId: string
  }>()
  const preview = !!templateId
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  const roles = useRoles()
  const currentSpaceId = useAppSelector(getCurrentSpaceId)
  const dataSourceMappingFetchState = useAppSelector(getAppBuilderDataSourcesMappingData)

  const featureFetchState = useAppSelector(getAppBuilderFeatureData)
  const currentUser = useAppSelector(getCurrentUser)
  const featureExecutionFetchState = useAppSelector(getAppBuilderFeatureExecutionData)
  const [variables, setVariables] = useState<any>([])
  const [elements, setElements] = useState<FlowElement[]>([])
  const [selectedElement, setSelectedElement] = useState<FlowElement | undefined>(defaultSelectedElement)
  const [elementsHaveBeenSelected, setElementsHaveBeenSelected] = useState<string[]>([])
  const [workflowValidationErrors, setWorkflowValidationErrors] = useState<BasicValidationError[]>([])
  const [stepFormHasChanges, setStepFormHasChanges] = useState(false)
  const [triggerSaved, setTriggerSaved] = useState(false)
  const [showFormChangesWarning, setShowFormChangesWarning] = useState(false)
  const dispatch = useAppDispatch()

  const isValidatingFeature =
    featureFetchState.isExecuting ||
    (featureFetchState.isExecutionCompleted &&
      (featureExecutionFetchState.isLoading ||
        featureExecutionFetchState.featureExecution?.status === ExecutionStepStatus.Pending))

  const appQueryResult = useQuery<App>({
    ...getFetchAppQueryOptions({ spaceId, appId }),
    enabled: !!appId && !preview
  })

  const featureQueryResponse = useQuery<Feature>({
    ...featureQueryOptions({ spaceId, appId, featureId }),
    enabled: !!appId && !preview
  })
  type WorkgridDatasourceInstanceQueries = UseQueryOptions<WorkgridDatasource>[]
  const dataSourceInstancesQuery = useQueries<WorkgridDatasourceInstanceQueries>(
    appQueryResult?.data?.dataSources?.map(ds => ({
      ...dataSourceV3QueryOptions({
        spaceId,
        datasourceId: ds.key,
        instanceId: ds.id
      }),
      enabled: !!appQueryResult?.data?.dataSources && !preview
    })) || []
  )

  type WorkgridCatalogDatasourceQueries = UseQueryOptions<WorkgridCatalogDatasource>[]
  const dataSourcesQuery = useQueries<WorkgridCatalogDatasourceQueries>(
    appQueryResult?.data?.dataSources?.map((ds: AppInstanceDataSource) => ({
      ...dataSourceV3CatalogQueryOptions({
        spaceId,
        datasourceId: ds.key
      }),
      enabled: !!appQueryResult?.data?.dataSources
    })) || []
  )

  const workgridDataSources = dataSourcesQuery.reduce(
    (workgridDataSources: WorkgridCatalogDatasource[], dataSource) => {
      if (dataSource.data) {
        workgridDataSources.push(dataSource.data)
      }
      return workgridDataSources
    },
    []
  )

  useEffect(
    () => {
      if (featureFetchState.isCompleted || preview) {
        setElements(
          highlightSelectedElement(
            convertFeatureToVisualWorkflow({
              workflow: featureFetchState.feature.workflow,
              dataSources: workgridDataSources,
              isValidatingFeature
            }) as FlowElement[],
            selectedElement
          )
        )
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      featureFetchState.feature.workflow,
      appQueryResult?.data?.dataSources?.length,
      appQueryResult?.data?.id,
      isValidatingFeature
    ]
  )

  useEffect(() => {
    const runValidationErrors = async () => {
      const validationDetails = await validateNodes(
        elements,
        featureFetchState.feature.workflow,
        featureFetchState.isCompleted,
        appQueryResult?.data?.features
      )
      setWorkflowValidationErrors(() => validationDetails.runValidationErrors)
    }
    if (featureFetchState.isCompleted && workflowValidationErrors?.length > 0) {
      runValidationErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, featureFetchState.isCompleted])

  useEffect(
    () => {
      if (featureFetchState.isCompleted || preview) {
        const workflow = cloneDeep(featureFetchState.feature.workflow)
        const variables = {} as Variables

        const isDataSourceEvent = workflow.trigger.type === TriggerTypes.dataSourceEvent
        const isNotificationAction = workflow.trigger.type === TriggerTypes.notificationAction
        const isScheduledEvent = workflow.trigger.type === TriggerTypes.scheduled
        const isHttpEvent = workflow.trigger.type === TriggerTypes.http
        const isFeatureTriggerEvent = workflow.trigger.type === TriggerTypes.featureTrigger
        const isMicroappViewEvent = workflow.trigger.type === TriggerTypes.microappView
        const isKnowledgeEvent = workflow.trigger.type === TriggerTypes.knowledge
        const isManualEvent = workflow.trigger.type === TriggerTypes.manual

        const featureVariables = buildFeatureVariables(
          appQueryResult?.data?.features?.filter(feature =>
            isMicroappViewEvent ? true : feature?.id !== featureFetchState.feature.id
          ),
          agentsFlagOn
        )

        if (featureVariables?.length > 0) {
          variables.feature = featureVariables
        }

        variables.app = buildApplicationVariables(appQueryResult?.data, agentsFlagOn)

        variables.config = buildConfigVariables(featureFetchState.feature.config)

        if (dataSourceInstancesQuery.every(query => query.data)) {
          variables.dataSource = buildDataSourceConfigVariables(
            // @ts-expect-error: TS2345 because "Argument of type 'UseQueryResult<WorkgridDatasource, unknown>[]' is not assignable to parameter of type '{ data: WorkgridDatasource; }[]'."
            dataSourceInstancesQuery,
            dataSourceMappingFetchState.dataSourcesMapping
          )
        }

        if (
          isDataSourceEvent &&
          workflow.trigger.triggerData.triggerEvent &&
          appQueryResult?.data?.dataSources &&
          appQueryResult?.data?.dataSources?.length > 0
        ) {
          variables.trigger = buildWorkgridDataSourceTriggerVariables(
            dataSourceMappingFetchState.dataSourcesMapping,
            workflow.trigger
          )
        }

        if (isNotificationAction && workflow.trigger.triggerData) {
          variables.trigger = buildNotificationActionTriggerVariables(workflow.trigger)
        }

        if (isScheduledEvent && workflow.trigger.triggerData) {
          variables.trigger = buildScheduledEventTriggerVariables(workflow.trigger, currentUser)
        }

        if (isMicroappViewEvent) {
          variables.trigger = buildMicroappEventVariables(workflow.trigger, currentUser)
        }

        if (isManualEvent) {
          variables.trigger = buildManualEventVariables(workflow.trigger, currentUser)
        }

        if (isHttpEvent && workflow.trigger.triggerData) {
          variables.trigger = buildHttpTriggerVariables(workflow.trigger)
        }

        if (isFeatureTriggerEvent && workflow.trigger.triggerData) {
          variables.trigger = buildFeatureTriggerVariables(workflow.trigger)
        }

        if (isKnowledgeEvent) {
          variables.trigger = buildKnowledgeEventVariables(workflow.trigger, currentUser)
        }

        const assignStepVariables = (steps: Step[]) => {
          traverseSteps(steps, (step: Step) => {
            const stepElement = elements.find((ele: FlowElement) => ele.id === step.id)

            if (step.output) {
              const isArray = step.output.type === FieldTypes.ARRAY
              const stepVariableName = `$.step-${step.stepCount}.output`
              let stepVariables = [] as any
              if (step.output.properties) {
                const variableStepData = {
                  stepName: step.name,
                  stepLabel: stepElement?.data.label,
                  stepType: step.type,
                  icon: stepElement?.data.icon,
                  iconUrl: stepElement?.data.iconUrl
                }
                stepVariables = getVariablesFromJsonSchema(
                  variableStepData,
                  step.output.properties,
                  stepVariableName,
                  isArray
                )
              }
              const initialStep = {
                name: stepVariableName,
                stepName: step.name,
                stepLabel: stepElement?.data.label,
                stepType: step.type,
                example: step.output.example,
                displayName: stepElement?.data.config.name,
                type: step.output.type,
                icon: stepElement?.data.iconUrl || stepElement?.data.icon
              }

              stepVariables.unshift(initialStep)
              if (stepVariables?.length > 0) {
                variables[step.id] = stepVariables
              }
            }

            if (step.type === StepType.DataSource) {
              const dataSourceVariables = buildDataSourceStepVariables({
                step,
                stepElement,
                dataSourcesMapping: dataSourceMappingFetchState.dataSourcesMapping,
                isWorkgridDataSource: false
              })
              if (dataSourceVariables?.length > 0) {
                variables[step.id] = dataSourceVariables
              }
            }

            if (step.type === StepType.WorkgridDataSource && !step.output) {
              const dataSourceVariables = buildDataSourceStepVariables({
                step,
                stepElement,
                dataSourcesMapping: dataSourceMappingFetchState.dataSourcesMapping,
                isWorkgridDataSource: true
              })
              if (dataSourceVariables?.length > 0) {
                variables[step.id] = dataSourceVariables
              }
            }

            // Define default step variables if there are none
            if (variables[step.id] === undefined) {
              if (step.type === StepType.APIRequest)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'API Request')

              if (step.type === StepType.CustomCode)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'Custom Code')

              if (step.type === StepType.WorkgridDataSource)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'Data Source')

              if (step.type === StepType.DataStorage)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'Data Storage')

              if (step.type === StepType.NotificationDateFormatter)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'Date Formatter', FieldTypes.STRING)

              if (step.type === StepType.FileUpload)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'File Upload')

              if (step.type === StepType.FileDownload)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'File Download')

              if (step.type === StepType.DocumentTextSplitter)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'Document Text Splitter')

              if (step.type === StepType.AdaptiveCard)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'Adaptive Card')

              if (step.type === StepType.DocumentSearchStorage)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'Document Search Storage')

              if (step.type === StepType.DataTransform) {
                let actionName
                if (step.stepData?.actionName) {
                  actionName = getOperations(featureFlags).find(
                    op =>
                      op.name === StepType.DataTransform && op.defaultStepData?.actionName === step.stepData.actionName
                  )?.name
                }
                variables[step.id] = buildDefaultStepVariables(step, stepElement, actionName || 'Data Transform')
              }

              if (step.type === StepType.InvokeLambda)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'Invoke Lambda')

              if (step.type === StepType.GenerateMicroappToken)
                variables[step.id] = buildDefaultStepVariables(step, stepElement, 'Generate Microapp Token')
            }

            if (step.type === StepType.Loop) {
              const triggerVariables = isDataSourceEvent && !!variables.trigger ? variables.trigger : undefined
              const inputStep = getLoopInputStep(step, workflow)
              const loopVariables = buildLoopStepVariables(
                step,
                stepElement,
                dataSourceMappingFetchState.dataSourcesMapping,
                inputStep,
                triggerVariables
              )
              if (loopVariables?.length > 0) {
                variables[step.id] = loopVariables
              }
            }
            return step
          })
        }
        assignStepVariables(workflow.steps)
        setVariables(variables)
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
      dataSourceMappingFetchState.dataSourcesMapping,
      appQueryResult?.data?.features?.length,
      featureFetchState.feature.workflow,
      elements
    ]
  )

  useEffect(() => {
    if (featureExecutionFetchState.isCompleted && has(featureExecutionFetchState, 'featureExecution.stepExecutions')) {
      onExecuteFeatureValidation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureExecutionFetchState.isCompleted])

  const getVariablesForStep = () => {
    if (selectedElement && !isEmpty(variables)) {
      const stepId = selectedElement.id
      const stepPath = findPath(featureFetchState.feature.workflow, stepId) //find node path in workflow by id
      const parentPath = removeLastPath(stepPath) //need to remove last path to get back to the object
      const variablesForStep: any[] = []

      if (variables['trigger']) {
        variablesForStep.push(variables['trigger'])
      }

      if (variables['feature']) {
        variablesForStep.push(variables['feature'])
      }

      if (variables['app']) {
        variablesForStep.push(variables['app'])
      }

      if (variables['config']) {
        variablesForStep.push(variables['config'])
      }

      if (variables['dataSource']) {
        variablesForStep.push(variables['dataSource'])
      }

      breadth({
        tree: [featureFetchState.feature.workflow.steps],
        visit(step: Step) {
          // @ts-expect-error
          if (!step[0] && variables[step.id]) {
            variablesForStep.push(variables[step.id])
          }
        },
        filter(step: Step) {
          const currentNode = get(featureFetchState.feature.workflow, parentPath)
          if (
            step.type !== StepType.DataSource &&
            step.type !== StepType.WorkgridDataSource &&
            step.type !== StepType.Loop &&
            step.type !== StepType.CustomCode &&
            step.type !== StepType.NotificationDateFormatter &&
            step.type !== StepType.Boolean &&
            step.type !== StepType.APIRequest &&
            step.type !== StepType.DataStorage &&
            step.type !== StepType.DataTransform &&
            step.type !== StepType.RunFeature &&
            step.type !== StepType.UserLookup &&
            step.type !== StepType.FileUpload &&
            step.type !== StepType.FileDownload &&
            step.type !== StepType.DocumentTextSplitter &&
            step.type !== StepType.AdaptiveCard &&
            step.type !== StepType.SetConfigVariable &&
            step.type !== StepType.DocumentSearchStorage &&
            step.type !== StepType.DeployFeature &&
            step.type !== StepType.InvokeLambda &&
            step.type !== StepType.GenerateMicroappToken
          ) {
            return false
          }

          if (step.type === StepType.Loop) {
            return !!findNestedObject(step.stepData?.loopSteps, 'id', currentNode?.id)
          }

          return step?.stepCount && step?.stepCount < currentNode?.stepCount
        },
        getChildren(step: Step) {
          const trueSteps = step.stepData?.onTrue || []
          const falseSteps = step.stepData?.onFalse || []

          // @ts-expect-error
          return step[0] || step.stepData.loopSteps || [...trueSteps, ...falseSteps]
        }
      })
      return variablesForStep
    } else return []
  }

  const getLoopInputStep = (
    step: Step,
    workflow: Workflow,
    inputVariablePath: string = ''
  ): Step | TriggerInput | undefined => {
    const inputVariable = step.stepData.inputVariable
    const parseInputVariable = inputVariable.trim().split('.')
    if (parseInputVariable[1] === 'trigger') {
      return {
        type: TriggerTypes.dataSourceEvent,
        inputVariablePath: inputVariablePath || inputVariable
      }
    }

    const inputStep = flattenFeatureSteps(workflow.steps).find(
      (step: Step) => `step-${step.stepCount}` === parseInputVariable[1]
    )

    if (inputStep?.type === StepType.Loop && inputStep.stepData.inputVariable !== inputVariable) {
      const inputVariablePath = inputStep?.stepData.inputVariable.concat(inputVariable.trim().split('output')[1])
      return getLoopInputStep(inputStep, workflow, inputVariablePath)
    }
    return inputStep
  }

  const highlightSelectedElement = (elements: FlowElement[], selectedElement: FlowElement | undefined) => {
    return elements?.map((element: FlowElement) => {
      if (selectedElement && element.id === selectedElement.id) {
        let selectedStyles = {}
        switch (selectedElement.type) {
          case CustomNodes.stepNode: {
            selectedStyles = {
              border: '2px solid #1A56DB',
              boxShadow: '0px 0px 5px 0px #1A56DB'
            }
            break
          }
          case CustomNodes.placeHolderNode: {
            selectedStyles = {
              boxShadow: '2px solid rgb(4, 73, 213)'
            }
            break
          }
        }

        element.style = {
          ...element.style,
          ...selectedStyles
        }
        setElementsHaveBeenSelected((arr: any) => [...arr, selectedElement.id])
      } else {
        if (
          (element.type !== CustomNodes.placeHolderNode &&
            !workflowValidationErrors.some(error => error.id === element.id)) ||
          elementsHaveBeenSelected.includes(element.id)
        ) {
          element.style = { ...element.style, boxShadow: 'none', border: '2px solid #DDDDDD' }
        }
      }
      return element
    })
  }

  const onSelectElement = (element: FlowElement | undefined) => {
    setSelectedElement(element)
    setElements(highlightSelectedElement(elements, element))
  }

  const onSetStepFormHasChanges = (value: boolean) => setStepFormHasChanges(value)

  const onSetTriggerSaved = (value: boolean) => setTriggerSaved(value)

  const saveFeature = async (workflow: Workflow) => {
    const feature = omit(featureFetchState.feature, ['createdBy', 'modifiedBy', 'createdDate', 'modifiedDate'])

    if (workflow.trigger.type === TriggerTypes.knowledge) {
      feature.isQueryable = workflow.trigger.triggerData.isQueryable
      feature.responseType = workflow.trigger.triggerData.responseType
    }
    await dispatch(
      actions.updateAppInstanceFeature({
        instanceId: appId,
        feature: { ...feature, workflow }
      } as UpdateFeatureProps)
    )
    // Temporary until we cut over to use react-query entirely
    await featureQueryResponse.refetch()
  }

  const updateElements = async (element: FlowElement, values: any) => {
    const workflow = cloneDeep(featureFetchState.feature.workflow)
    if (element.type === CustomNodes.triggerNode) {
      delete values.stepData
      delete values.triggerData.errorConfig //only needed for UI display
      if (workflow.trigger.type === TriggerTypes.manual) {
        delete values.triggerData
      }
      workflow.trigger = {
        ...workflow.trigger,
        ...values
      }
    } else {
      delete values.triggerData
      delete values.stepData.errorConfig //only needed for UI display
      const updatePath = findPath(workflow, element.id)
      //need to remove last path to get back to the object to update rather than the position of the id within the object.
      const parentPath = removeLastPath(updatePath)
      set(workflow, parentPath, {
        ...get(workflow, parentPath),
        ...values
      })
    }
    await saveFeature(workflow)
  }

  const onDeleteElement = async (element: FlowElement | Step | TriggerConfig) => {
    const workflow = cloneDeep(featureFetchState.feature.workflow)
    const type = get(element, 'type')

    if (type === CustomNodes.triggerNode || Object.values(TriggerTypes)?.includes(type as TriggerTypes)) {
      workflow.trigger = {
        type: TriggerTypes.none
      }
      // delete previous trigger data values
      clearLocalStorageTriggerData(featureFetchState.feature.id)
    } else {
      //find node path in workflow by id
      const deletePath = findPath(workflow, get(element, 'id', ''))
      //need to remove last path to get back to the object to delete rather than the position of the id within the object.
      const parentPath = removeLastPath(deletePath)
      //remove node from workflow given path to node.
      unset(workflow, parentPath)
      //unset will leave a null in place of where it deletes an object from an array so using compact here to remove the null entry
      update(workflow, removeLastPath(parentPath), compact)
    }
    workflow.steps = constructOnSuccessChain(workflow.steps)
    await saveFeature(workflow)
  }

  const onDuplicateStep = async (step: Step) => {
    const workflow = cloneDeep(featureFetchState.feature.workflow)
    //find node path in workflow by id
    const duplicatePath = findPath(workflow, get(step, 'id', ''))
    //need to remove last path to get back to the step object to duplicate rather than the position of the id within the object
    const stepPath = removeLastPath(duplicatePath)
    //need to remove last path to get back the parent path (may be nested within workflow)
    const parentPath = removeLastPath(stepPath)

    //get the index within the step array of the step to duplicate from the path
    const index = Number.parseInt(trim(stepPath.slice(stepPath.lastIndexOf('.')), '.[]'))
    //clone the parent step array containing the step to duplicate
    const steps = cloneDeep(get(workflow, parentPath))

    //duplicate step
    let stepToDuplicate = cloneDeep(get(workflow, stepPath))
    stepToDuplicate = updateDuplicateStepIDs([stepToDuplicate])[0]
    stepToDuplicate.name = stepToDuplicate.name.concat(' ', '(copy)')

    //insert duplicate step in the steps array
    steps.splice(index + 1, 0, stepToDuplicate)

    //update steps within the workflow using the parent path and reassign step count
    set(workflow, parentPath, steps)
    workflow.steps = assignStepCount(workflow.steps)

    if (stepToDuplicate.type === StepType.Loop || stepToDuplicate.type === StepType.Boolean) {
      //replace variable references of nested duplicate steps
      const updatedSteps = cloneDeep(get(workflow, parentPath))
      const updatedStepsWithReplacedVariables = updateNestedStepVariableReferences(updatedSteps, index + 1)
      set(workflow, parentPath, updatedStepsWithReplacedVariables)
    }

    workflow.steps = constructOnSuccessChain(workflow.steps)
    await saveFeature(workflow)
  }

  const onRunValidation = async () => {
    const validationDetails = await validateNodes(
      elements,
      featureFetchState.feature.workflow,
      featureFetchState.isCompleted,
      appQueryResult?.data?.features
    )
    const workflowClone = cloneDeep(featureFetchState.feature.workflow)

    if (validationDetails.runValidationErrors.some(error => error.key === CustomNodes.triggerNode)) {
      workflowClone.trigger.errorConfig = setElementErrorConfig({
        hasErrors: true,
        showValid: false,
        showValidExecution: false
      })
    } else {
      workflowClone.trigger.errorConfig = setElementErrorConfig({
        hasErrors: false,
        showValid: true,
        showValidExecution: false
      })
    }

    workflowClone.steps = traverseSteps(workflowClone.steps, (step: Step) => {
      if (validationDetails.runValidationErrors.some(error => error.id === step.id)) {
        step.errorConfig = setElementErrorConfig({ hasErrors: true, showValid: false, showValidExecution: false })
      } else {
        step.errorConfig = setElementErrorConfig({ hasErrors: false, showValid: true, showValidExecution: false })
      }
      return step
    })

    setElements(
      convertFeatureToVisualWorkflow({
        workflow: workflowClone,
        dataSources: workgridDataSources,
        isValidatingFeature
      }) as FlowElement[]
    )
    setElementsHaveBeenSelected([])
    setWorkflowValidationErrors(validationDetails.runValidationErrors)

    return validationDetails.workflowIsValid
  }

  interface ElementErrorConfig {
    hasErrors: boolean
    showValid: boolean
    showValidExecution: boolean
  }

  const setElementErrorConfig = ({ hasErrors, showValid, showValidExecution }: ElementErrorConfig) => {
    return {
      hasErrors,
      showValid,
      showValidExecution
    }
  }

  const onExecuteFeatureValidation = () => {
    const stepExecutions = featureExecutionFetchState.featureExecution?.stepExecutions
    const workflowClone = cloneDeep(featureFetchState.feature.workflow)

    if (
      stepExecutions.some(
        (stepExecution: FeatureExecutionStep) =>
          stepExecution.stepName === 'trigger' && stepExecution.status === ExecutionStepStatus.Failed
      )
    ) {
      workflowClone.trigger.errorConfig = setElementErrorConfig({
        hasErrors: true,
        showValid: false,
        showValidExecution: false
      })
    } else {
      workflowClone.trigger.errorConfig = setElementErrorConfig({
        hasErrors: false,
        showValid: false,
        showValidExecution: true
      })
    }

    workflowClone.steps = traverseSteps(workflowClone.steps, (step: Step) => {
      if (
        stepExecutions.some(
          (stepExecution: FeatureExecutionStep) =>
            stepExecution.stepName !== 'trigger' &&
            stepExecution.status === ExecutionStepStatus.Failed &&
            stepExecution.stepName.includes(step.id)
        )
      ) {
        step.errorConfig = setElementErrorConfig({ hasErrors: true, showValid: false, showValidExecution: false })
      } else if (
        stepExecutions.some(
          (stepExecution: FeatureExecutionStep) =>
            stepExecution.stepName !== 'trigger' &&
            stepExecution.status === ExecutionStepStatus.Success &&
            stepExecution.stepName.includes(step.id)
        )
      ) {
        step.errorConfig = setElementErrorConfig({ hasErrors: false, showValid: false, showValidExecution: true })
      }
      return step
    })

    setElements(
      convertFeatureToVisualWorkflow({
        workflow: workflowClone,
        dataSources: workgridDataSources,
        isValidatingFeature
      }) as FlowElement[]
    )
    setElementsHaveBeenSelected([])
  }

  const isFeatureActive = () => {
    return featureQueryResponse?.data?.status === FeatureStatus.ACTIVE
  }

  const isFeaturePreview = () => {
    return featureFetchState.feature?.status === FeatureStatus.PREVIEW
  }

  const hasEditPermissions = () => {
    const hasEditPermissions =
      roles.hasWorkshopAppOwnerRole(currentSpaceId) && isAppOwner(appQueryResult?.data, currentUser?.id)
    return hasEditPermissions
  }

  const featureExecuted =
    featureFetchState.isExecutionSuccessful &&
    featureExecutionFetchState.featureExecution?.status === ExecutionStepStatus.Success

  const featureContext = {
    elements,
    onSelectElement,
    updateElements,
    selectedElement,
    onDeleteElement,
    onDuplicateStep,
    featureExecuted,
    workflowValidationErrors,
    onRunValidation,
    variables,
    getVariablesForStep,
    stepFormHasChanges,
    onSetStepFormHasChanges,
    triggerSaved,
    onSetTriggerSaved,
    showFormChangesWarning,
    onShowFormHasChangesWarning: setShowFormChangesWarning,
    isFeatureActive,
    isFeaturePreview,
    hasEditPermissions
  }

  return <WorkflowFeatureContext.Provider value={featureContext}>{children}</WorkflowFeatureContext.Provider>
}

interface FeatureContext {
  elements: FlowElement[]
  onSelectElement: (element?: FlowElement) => void
  updateElements: (element: FlowElement, values: any) => void
  onDeleteElement: (element: FlowElement | Step | TriggerConfig) => void
  onDuplicateStep: (step: Step) => void
  selectedElement: FlowElement
  featureExecuted: boolean
  workflowValidationErrors: object[]
  onRunValidation: () => boolean
  variables: Variables
  getVariablesForStep: () => Variable[][]
  stepFormHasChanges: boolean
  onSetStepFormHasChanges: (value: boolean) => void
  triggerSaved: boolean
  onSetTriggerSaved: (value: boolean) => void
  showFormChangesWarning: boolean
  onShowFormHasChangesWarning: (value: boolean) => void
  isFeatureActive: () => boolean
  isFeaturePreview: () => boolean
  hasEditPermissions: () => boolean
}

const useWorkflowFeature = (): FeatureContext => {
  const context = useContext(WorkflowFeatureContext) as FeatureContext
  if (!context) throw new Error('useWorkflowFeature must be used within a WorkflowFeatureContext')
  return context
}

export const WorkflowFeatureProvider = WorkflowFeatureProviderBase

export { useWorkflowFeature, WorkflowFeatureContext }
